import { useCallback, useMemo, useState } from 'react';

import { Carousel, Embla, useAnimationOffsetEffect } from '@mantine/carousel';
import { AspectRatio, Box, Flex, Skeleton, Stack, useMantineTheme } from '@mantine/core';
import { useElementSize, useMediaQuery } from '@mantine/hooks';
import { IconChevronLeft, IconChevronRight } from 'ui-icons';

import { ImageMantine } from '../../../DataDisplay';
import { cx } from '../../../Provider/Provider';
import { DEFAULT_IMAGE_PLACEHOLDER } from '../../../config';
import classes from './CarouselWithPreview.module.css';

const PreviewCarousel = ({
  slides,
  header,
  activeSlide,
  setActiveSlide,
  isMobile,
  ratio = 3 / 2,
}: {
  slides?: string[];
  header?: React.ReactNode;
  activeSlide: number;
  setActiveSlide: React.Dispatch<React.SetStateAction<number>>;
  isMobile: boolean | undefined;
  ratio?: number;
}) => {
  return (
    <Box pos="relative">
      {slides && slides.length >= 1 ? (
        <Carousel
          initialSlide={activeSlide}
          onSlideChange={setActiveSlide}
          withControls={false}
          draggable={isMobile}
          withIndicators={isMobile}
          containScroll={isMobile ? 'trimSnaps' : undefined}
          loop
          classNames={{ indicators: classes.indicators, indicator: classes.indicator }}
        >
          {slides.map((src) => (
            <Carousel.Slide key={src}>
              <AspectRatio ratio={ratio}>
                <ImageMantine src={src} radius="lg" className={classes.slide_img} />
              </AspectRatio>
            </Carousel.Slide>
          ))}
        </Carousel>
      ) : (
        <AspectRatio ratio={ratio}>
          <ImageMantine src={DEFAULT_IMAGE_PLACEHOLDER} radius="lg" className={classes.slide_img} />
        </AspectRatio>
      )}
      <Flex w="100%" px="md" pos="absolute" top={16}>
        {header}
      </Flex>
    </Box>
  );
};

const SlidesCarousel = ({
  slides,
  activeSlide,
  setActiveSlide,
  slideSize,
}: {
  slides?: string[];
  activeSlide: number;
  setActiveSlide: React.Dispatch<React.SetStateAction<number>>;
  slideSize: number;
}) => {
  const [embla, setEmbla] = useState<Embla | null>();

  useAnimationOffsetEffect(embla, 200);

  const handleOnPreviousSlide = useCallback(() => {
    if (!slides) return null;

    if (activeSlide === 0) {
      setActiveSlide(slides?.length - 1);
    } else {
      setActiveSlide(activeSlide - 1);
    }
  }, [activeSlide, setActiveSlide, slides]);

  const handleOnNextSlide = useCallback(() => {
    if (!slides) return null;

    if (activeSlide === slides?.length - 1) {
      setActiveSlide(0);
    } else {
      setActiveSlide(activeSlide + 1);
    }
  }, [activeSlide, setActiveSlide, slides]);

  const hasMoreSlides = useMemo(() => {
    return slides && slides?.length > 6;
  }, [slides]);

  return (
    <Carousel
      getEmblaApi={setEmbla}
      align="start"
      loop
      slideSize={slideSize}
      draggable={hasMoreSlides}
      nextControlIcon={<IconChevronRight />}
      previousControlIcon={<IconChevronLeft />}
      nextControlProps={{ onClick: handleOnNextSlide, onKeyDown: handleOnNextSlide }}
      previousControlProps={{ onClick: handleOnPreviousSlide, onKeyDown: handleOnPreviousSlide }}
      dragFree
      classNames={{
        container: cx(classes.container, { [classes['container--noScroll']]: !hasMoreSlides }),
        slide: classes.slide,
        controls: classes.controls,
        control: classes.control,
      }}
      // in order to ensure that the space (ie. the Carousel root and viewport) in which our slides can scroll will only fit and show 6 slides
      // we will multiply the size of each slide times the total number, 6, and add back the total space between them. ie. 4px gap * 5
      w={slideSize * 6 + 20}
      mx="auto"
    >
      {slides?.map((src, index) => (
        <Carousel.Slide key={src} onClick={() => setActiveSlide(index)}>
          <ImageMantine
            src={src}
            radius="sm"
            w={activeSlide === index ? slideSize - 4 : slideSize}
            h={activeSlide === index ? slideSize - 4 : slideSize}
            className={cx(classes.slide_img, {
              [classes['slide_img--active']]: activeSlide === index,
            })}
          />
        </Carousel.Slide>
      ))}
    </Carousel>
  );
};

const CarouselWithPreview = ({
  slides,
  header,
  ratio,
}: {
  slides?: string[];
  header?: React.ReactNode;
  ratio?: number;
}) => {
  const theme = useMantineTheme();
  const isMobile = useMediaQuery(`(max-width: ${theme.breakpoints.sm})`);
  const { ref, width } = useElementSize();
  const [activeSlide, setActiveSlide] = useState<number>(0);

  const showMoreImages = slides && slides.length > 1;

  const slideSize = useMemo(() => {
    // to remove the extra space included from the controls and spacing between the controls and slides, and each slide, we subtract 108px.
    // => 12px space between controls and slides; 4px gap between each slide
    // then, in order to define the size of each slide, we divide by 6, the number of slides we expect to see in view
    return (width - 108) / 6;
  }, [width]);

  return (
    <Skeleton visible={!slides}>
      <Stack gap="sm" ref={ref}>
        <PreviewCarousel
          slides={slides}
          header={header}
          setActiveSlide={setActiveSlide}
          activeSlide={activeSlide}
          isMobile={isMobile}
          ratio={ratio}
        />

        {!isMobile && showMoreImages && (
          <SlidesCarousel
            slides={slides}
            setActiveSlide={setActiveSlide}
            slideSize={slideSize}
            activeSlide={activeSlide}
          />
        )}
      </Stack>
    </Skeleton>
  );
};

export { CarouselWithPreview };
